import {useCallback, useEffect, useMemo, useState} from 'react';

const RELEASE_CHECK_INTERVAL_MINUTES = 15;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const RELEASE_CHECK_INTERVAL_MS = RELEASE_CHECK_INTERVAL_MINUTES * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
const VERSION_ENDPOINT = `${process.env.PUBLIC_URL || ''}/version.json`;

export interface AppReleaseMetadata {
    version: string;
    buildTime: string;
    cacheName?: string;
}

declare global {
    interface Window {
        __THE_HUDD_RELEASE__?: AppReleaseMetadata;
    }
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

const normalizeReleaseMetadata = (value: unknown): AppReleaseMetadata | null => {
    if (!isRecord(value)) {
        return null;
    }

    if (typeof value.version !== 'string' || typeof value.buildTime !== 'string') {
        return null;
    }

    return {
        version: value.version,
        buildTime: value.buildTime,
        cacheName: typeof value.cacheName === 'string' ? value.cacheName : undefined,
    };
};

export const getInstalledReleaseMetadata = (): AppReleaseMetadata | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    return normalizeReleaseMetadata(window.__THE_HUDD_RELEASE__);
};

export const fetchLatestReleaseMetadata = async (): Promise<AppReleaseMetadata | null> => {
    const response = await fetch(VERSION_ENDPOINT, {
        cache: 'no-store',
        headers: {
            'Cache-Control': 'no-cache',
        },
    });

    if (!response.ok) {
        return null;
    }

    const payload = await response.json();
    return normalizeReleaseMetadata(payload);
};

export const isSameRelease = (
    installedRelease: AppReleaseMetadata | null,
    latestRelease: AppReleaseMetadata | null
): boolean => {
    if (!installedRelease || !latestRelease) {
        return false;
    }

    return installedRelease.version === latestRelease.version
        && installedRelease.buildTime === latestRelease.buildTime
        && installedRelease.cacheName === latestRelease.cacheName;
};

export const formatReleaseTimestamp = (value?: string | null): string => {
    if (!value) {
        return 'Onbekend';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return 'Onbekend';
    }

    return new Intl.DateTimeFormat('nl-NL', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(parsed);
};

type UseAppReleaseOptions = {
    enableAutoRefresh?: boolean;
};

type UseAppReleaseResult = {
    installedRelease: AppReleaseMetadata | null;
    latestRelease: AppReleaseMetadata | null;
    isChecking: boolean;
    isUpdateAvailable: boolean;
    lastCheckedAt: string | null;
    refreshReleaseInfo: () => Promise<void>;
};

export const useAppRelease = (options?: UseAppReleaseOptions): UseAppReleaseResult => {
    const enableAutoRefresh = options?.enableAutoRefresh !== false;
    const [installedRelease, setInstalledRelease] = useState<AppReleaseMetadata | null>(() => getInstalledReleaseMetadata());
    const [latestRelease, setLatestRelease] = useState<AppReleaseMetadata | null>(null);
    const [isChecking, setIsChecking] = useState<boolean>(false);
    const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);

    const refreshReleaseInfo = useCallback(async () => {
        setIsChecking(true);

        try {
            setInstalledRelease(getInstalledReleaseMetadata());
            const latestMetadata = await fetchLatestReleaseMetadata();
            setLatestRelease(latestMetadata);
            setLastCheckedAt(new Date().toISOString());
        } finally {
            setIsChecking(false);
        }
    }, []);

    useEffect(() => {
        refreshReleaseInfo().catch((error) => {
            console.warn('[Release] Release metadata ophalen mislukt.', error);
        });
    }, [refreshReleaseInfo]);

    useEffect(() => {
        if (!enableAutoRefresh) {
            return;
        }

        const handleVisible = () => {
            if (document.visibilityState === 'visible') {
                refreshReleaseInfo().catch((error) => {
                    console.warn('[Release] Release metadata herladen mislukt.', error);
                });
            }
        };

        const handleOnline = () => {
            refreshReleaseInfo().catch((error) => {
                console.warn('[Release] Release metadata herladen mislukt.', error);
            });
        };

        document.addEventListener('visibilitychange', handleVisible);
        window.addEventListener('focus', handleOnline);
        window.addEventListener('online', handleOnline);

        const intervalId = window.setInterval(() => {
            refreshReleaseInfo().catch((error) => {
                console.warn('[Release] Periodieke release check mislukt.', error);
            });
        }, RELEASE_CHECK_INTERVAL_MS);

        return () => {
            document.removeEventListener('visibilitychange', handleVisible);
            window.removeEventListener('focus', handleOnline);
            window.removeEventListener('online', handleOnline);
            window.clearInterval(intervalId);
        };
    }, [enableAutoRefresh, refreshReleaseInfo]);

    const isUpdateAvailable = useMemo(() => {
        if (!installedRelease || !latestRelease) {
            return false;
        }

        return !isSameRelease(installedRelease, latestRelease);
    }, [installedRelease, latestRelease]);

    return {
        installedRelease,
        latestRelease,
        isChecking,
        isUpdateAvailable,
        lastCheckedAt,
        refreshReleaseInfo,
    };
};